import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, Stack, Chip, IconButton,
  Divider, Select, MenuItem, Button, FormControl, InputLabel, Tooltip,
  CircularProgress, Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Lock, LockOpen } from '@mui/icons-material';
import featureService from '../../services/featureService';
import billingService from '../../services/billingService';

const FEATURE_META = {
  workbooks: { label: 'Workbooks', desc: 'Visual automation & synthetic workflow designer.' },
  vera: { label: 'Vera', desc: 'Conversational AI assistant for the platform.' },
  sentinel: { label: 'Sentinel', desc: 'AI control plane / autonomous SRE.' },
  inventory: { label: 'Inventory', desc: 'Software estate: entity model, usage tracking, shadow-IT & spend.' },
};

/**
 * Operator control for who can use Workbooks / Vera / Sentinel:
 *  - flip the global default (enabled for all / disabled for all)
 *  - grant or revoke for a specific tenant (override the default)
 */
const OperatorFeatureAccessPage = () => {
  const [flags, setFlags] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [addState, setAddState] = useState({}); // feature -> { orgId, enabled }

  const load = useCallback(async () => {
    setError(null);
    try {
      const [flagData, orgsRes] = await Promise.all([
        featureService.listFeatureFlags(),
        billingService.listAllOrgs(),
      ]);
      setFlags(flagData);
      setOrgs(orgsRes.data || []);
    } catch (e) {
      setError('Failed to load feature access settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  const toggleDefault = (feature, enabledByDefault) =>
    run(() => featureService.setDefault(feature, enabledByDefault));

  const addOverride = (feature) => {
    const s = addState[feature];
    if (!s || !s.orgId) return;
    run(() => featureService.setOverride(feature, s.orgId, !!s.enabled))
      .then(() => setAddState((p) => ({ ...p, [feature]: { orgId: '', enabled: true } })));
  };

  const removeOverride = (feature, orgId) =>
    run(() => featureService.clearOverride(feature, orgId));

  const flipOverride = (feature, orgId, enabled) =>
    run(() => featureService.setOverride(feature, orgId, enabled));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Feature Access</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control which organizations can use each premium module. Set a global default,
        then grant or revoke access for individual tenants.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        {flags.map((f) => {
          const meta = FEATURE_META[f.feature] || { label: f.feature, desc: '' };
          const overriddenOrgIds = new Set((f.overrides || []).map((o) => o.orgId));
          const availableOrgs = orgs.filter((o) => !overriddenOrgIds.has(o.id));
          const add = addState[f.feature] || { orgId: '', enabled: true };
          return (
            <Card key={f.feature} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" fontWeight={600}>{meta.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{meta.desc}</Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      color={f.enabledByDefault ? 'success' : 'default'}
                      label={f.enabledByDefault ? 'Enabled for all' : 'Disabled for all'}
                    />
                    <Tooltip title={f.enabledByDefault ? 'Disable for all tenants by default' : 'Enable for all tenants by default'}>
                      <Switch
                        checked={f.enabledByDefault}
                        disabled={busy}
                        onChange={(e) => toggleDefault(f.feature, e.target.checked)}
                      />
                    </Tooltip>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Per-tenant overrides
                </Typography>

                {(f.overrides || []).length === 0 && (
                  <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
                    No overrides — every tenant follows the default above.
                  </Typography>
                )}

                <Stack spacing={1} sx={{ mb: 2 }}>
                  {(f.overrides || []).map((o) => (
                    <Stack key={o.orgId} direction="row" alignItems="center" spacing={1}
                           sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                      {o.enabled
                        ? <LockOpen fontSize="small" color="success" />
                        : <Lock fontSize="small" color="error" />}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{o.orgName}</Typography>
                        {o.orgSlug && <Typography variant="caption" color="text.secondary">{o.orgSlug}</Typography>}
                      </Box>
                      <Chip
                        size="small"
                        label={o.enabled ? 'Granted' : 'Revoked'}
                        color={o.enabled ? 'success' : 'error'}
                        variant="outlined"
                        onClick={() => flipOverride(f.feature, o.orgId, !o.enabled)}
                        disabled={busy}
                      />
                      <Tooltip title="Remove override (revert to default)">
                        <IconButton size="small" disabled={busy}
                                    onClick={() => removeOverride(f.feature, o.orgId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Add tenant</InputLabel>
                    <Select
                      label="Add tenant"
                      value={add.orgId}
                      onChange={(e) => setAddState((p) => ({ ...p, [f.feature]: { ...add, orgId: e.target.value } }))}
                    >
                      {availableOrgs.map((o) => (
                        <MenuItem key={o.id} value={o.id}>{o.name} ({o.slug})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Access</InputLabel>
                    <Select
                      label="Access"
                      value={add.enabled ? 'grant' : 'revoke'}
                      onChange={(e) => setAddState((p) => ({ ...p, [f.feature]: { ...add, enabled: e.target.value === 'grant' } }))}
                    >
                      <MenuItem value="grant">Grant</MenuItem>
                      <MenuItem value="revoke">Revoke</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={busy || !add.orgId}
                    onClick={() => addOverride(f.feature)}
                  >
                    Add
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OperatorFeatureAccessPage;
